import { DeleteOutlined }             from '@ant-design/icons';
import { EditOutlined }               from '@ant-design/icons';
import type { FilterValue }           from 'antd/es/table/interface';
import { withNavigation }             from 'components/App/withNavigation';
import { WithNavigationProps }        from 'components/App/withNavigation';
import React                          from 'react';
import { Button }                     from 'antd';
import { Table }                      from 'antd';
import type { TablePaginationConfig } from 'antd';
import type { ColumnType }            from 'antd/es/table';
import { observer }                   from 'mobx-react';
import { PagedCollection }            from 'collections/PagedCollection';
import { AppForm }                    from 'components/App/AppForm';
import DropDownMenu                   from 'components/App/DropDownMenu';
import LayoutTypeModel                from 'models/LayoutTypeModel';
import FormattedMessageWrapper        from 'components/App/FormattedMessageWrapper';
import PageTitle                      from 'components/App/PageTitle';
import handleApiError                 from 'utils/handleApiError';
import { formatMessageIntl }          from 'utils/messageIntl';
import modalConfirmDelete             from 'utils/modalConfirmDelete';
import rights                         from 'utils/rights';
import LayoutTypeForm                 from 'forms/LayoutTypeForm';

type LayoutTypeListState = {
  table: {
    filters: {
      search?: string;
    };
    itemsPerPage: number;
    page: number;
    sorts: Record<string, 'asc' | 'desc'>;
  };
};

@observer
class LayoutTypeList extends React.Component<WithNavigationProps> {
  public pagedCollLayoutType = new PagedCollection(LayoutTypeModel);

  public state: LayoutTypeListState = {
    table: {
      filters: {},
      itemsPerPage: 10,
      page: 1,
      sorts: {},
    },
  };

  public async componentDidMount() {
    if (!rights.for.layoutType.list) {
      this.props.navigate('/404');
      return;
    }

    await this._fetchData();
  }

  public render() {
    return (
      <>
        <PageTitle
          title={<FormattedMessageWrapper id="Aménagements" />}
          topRightWidget={this.renderTopRightWidget()}
        />

        <div className="accented-shadow" style={contentStyle}>
          <Table
            columns={this.columns}
            dataSource={[...this.pagedCollLayoutType.models]}
            loading={this.pagedCollLayoutType.isLoading}
            onChange={this._onChange}
            pagination={{
              current: this.pagedCollLayoutType.page,
              defaultCurrent: this.pagedCollLayoutType.page,
              hideOnSinglePage: true,
              pageSize: this.pagedCollLayoutType.itemsPerPage,
              placement: ['topEnd', 'bottomEnd'],
              size: 'medium',
              style: {
                paddingRight: 24,
              },
              total: this.pagedCollLayoutType.total,
            }}
            rowKey={model => model.get('id')}
            size="small"
          />
        </div>
      </>
    );
  }

  public renderTopRightWidget = () => (
    <>
      <Button
        htmlType="button"
        onClick={() => {
          AppForm.open(LayoutTypeForm, {
            model: new LayoutTypeModel(),
            onSuccess: async () => {
              await this._fetchData();
            },
          },
          {
            title: <FormattedMessageWrapper id="Ajouter un aménagement" />,
          });
        }}
      >
        <FormattedMessageWrapper id="Ajouter un aménagement" />
      </Button>
    </>
  );

  private _fetchData = async () => {
    this.pagedCollLayoutType.itemsPerPage = this.state.table.itemsPerPage;
    this.pagedCollLayoutType.filters = this.state.table.filters;
    this.pagedCollLayoutType.setPage(this.state.table.page);
    this.pagedCollLayoutType.sorts = this.state.table.sorts as Record<string, 'asc' | 'desc'>;

    await this.pagedCollLayoutType.list();
  };

  private _onChange = (
      pagination: TablePaginationConfig,
      _filters: Record<string, FilterValue | null>,
      sorter: any
  ) => {
    let sorts;
    if (!sorter.field || !sorter.order) {
      sorts = {};
    } else {
      const order = sorter.order === 'ascend' ? 'asc' : 'desc';
      sorts = {
        [sorter.field]: order,
      };
    }

    this.setState(
      {
        table: {
          filters: {},
          itemsPerPage: pagination.pageSize,
          page: pagination.current,
          sorts,
        },
      },
      () => this._fetchData(),
    );
  };

  public get columns(): ColumnType<LayoutTypeModel>[] {
    return [
      {
        dataIndex: 'label',
        sorter: true,
        title: <FormattedMessageWrapper id="Label" />,
      },
      {
        dataIndex: 'color',
        render: (_, record: LayoutTypeModel) => (
          <div
            style={{
              backgroundColor: record.color,
              borderRadius: '50%',
              height: 24,
              width: 24,
            }}
          />
        ),
        sorter: true,
        title: <FormattedMessageWrapper id="Couleur" />,
      },
      {
        dataIndex: 'actions',
        render: (_, record: LayoutTypeModel) => (
          <DropDownMenu
            items={[
              {
                icon: <EditOutlined />,
                key: 'layout_type_edit',
                label: <FormattedMessageWrapper id="Modifier" />,
                onClick: () => {
                  AppForm.open(LayoutTypeForm, {
                    model: record,
                    onSuccess: async () => {
                      await this._fetchData();
                    },
                  },
                  {
                    title: <FormattedMessageWrapper id="Modifier un aménagement" />,
                  });
                },
              },
              {
                danger: true,
                icon: <DeleteOutlined />,
                key: 'layout_type_delete',
                label: <FormattedMessageWrapper id="Supprimer" />,
                onClick: () => {
                  modalConfirmDelete({
                    onOk: async () => {
                      try {
                        await record.destroy();
                        await this._fetchData();
                      } catch (err) {
                        handleApiError(err);
                      }
                    },
                    title: formatMessageIntl("Êtes-vous sûr de vouloir supprimer cet aménagement ?"),
                  });
                },
              },
            ]}
            mode="icon"
          />
        ),
        title: <FormattedMessageWrapper id="Actions" />,
      },
    ];
  }
}

const contentStyle = {
  // background: '#fff',
  minHeight: 360,
  // padding: 24
};


const LayoutTypeListWithNavigation = withNavigation(LayoutTypeList);

export default LayoutTypeListWithNavigation;